import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { useGetEleveQuery, useUpdateEleveMutation } from "redux_dir/features/eleveApi";
import { useGetClassesAllQuery } from "redux_dir/features/classeApi";

/**
 * Hook personnalisé pour gérer la logique d'édition d'un enfant
 */
export const useEditEnfantLogic = () => {
  const { eleveId } = useParams();
  const navigate = useNavigate();
  
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Récupérer les données de l'élève
  const { 
    data: eleveData, 
    isLoading: isLoadingEleve,
    error: eleveError 
  } = useGetEleveQuery(eleveId, {
    skip: !eleveId,
  });

  // Récupérer les classes
  const { 
    data: classesData, 
    isLoading: isLoadingClasses,
    refetch: refetchClasses 
  } = useGetClassesAllQuery({});

  // Mutation pour mettre à jour l'élève
  const [updateEleve] = useUpdateEleveMutation();

  // Formulaire
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    control,
  } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      classeId: "",
      date_naissance: "",
    },
  });

  // Extraire les données de l'élève (gérer les deux structures possibles)
  const eleve = eleveData?.data || eleveData;

  // Mettre à jour le formulaire quand les données arrivent
  useEffect(() => {
    if (eleve) {
      reset({
        firstName: eleve.firstName || "",
        lastName: eleve.lastName || "",
        classeId: eleve.classeId?.toString() || eleve.classe?.id?.toString() || "",
        date_naissance: eleve.date_naissance 
          ? new Date(eleve.date_naissance).toISOString().split('T')[0] 
          : "",
      });
    }
  }, [eleve, reset]);

  /**
   * Organiser les classes dans l'ordre souhaité
   */
  const organiserClasses = (classes) => {
    if (!classes) return [];
    
    const ordreClasses = ["CI", "CP", "CE1", "CE2", "CM1", "CM2", "Collège", "Lycée"];
    
    return [...classes]
      .filter((classe) => 
        classe?.name?.toLowerCase()?.trim() !== "hv3eme" &&
        classe?.name?.toLowerCase()?.trim() !== "ocs4"
      )
      .sort((a, b) => {
        const indexA = ordreClasses.indexOf(a.name);
        const indexB = ordreClasses.indexOf(b.name);
        
        if (indexA === -1 && indexB === -1) return 0;
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
  };

  /**
   * Soumettre les modifications
   */
  const onSubmit = async (data) => {
    setIsSubmitting(true);
    
    try {
      const payload = {
        id: parseInt(eleveId),
        firstName: data.firstName.trim(),
        lastName: data.lastName.trim(),
        classeId: parseInt(data.classeId),
        date_naissance: data.date_naissance || null,
      };

      await updateEleve(payload).unwrap();
      
      toast.success("Profil de l'enfant modifié avec succès");
      navigate(-1);
    } catch (error) {
      console.error("Erreur lors de la modification:", error);
      toast.error("Erreur lors de la modification du profil");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Annuler et retourner
   */
  const handleCancel = () => {
    navigate(-1);
  };

  return {
    // État
    eleve,
    isLoadingEleve,
    isLoadingClasses,
    eleveError,
    isSubmitting,
    errors,
    
    // Données
    classes: organiserClasses(classesData?.data),
    
    // Fonctions
    register,
    control,
    handleSubmit,
    onSubmit,
    handleCancel,
    refetchClasses,
  };
};
